export const ROLES = {
  SUPER_ADMIN: "super_admin",
  SUPPORT_ADMIN: "support_admin",
  SUPPORT_ENGINEER: "support_engineer",
  BUSINESS_ADMIN: "business_admin",
  CLIENT_ADMIN: "client_admin",
  CLIENT_USER: "client_user",
  BUSINESS_USER: "business_user",
  APPROVER: "approver",
  VIEWER: "viewer",
};

// Mirrors the Roles & Permissions matrix returned with the signed-in user.
// The backend enforces it; this only hides actions the role cannot use.
export const hasPermission = (user, permission) =>
  user?.role === ROLES.SUPER_ADMIN ||
  (user?.permissions || []).includes(permission);

export const ROLE_LABEL = {
  super_admin: "Super Admin",
  support_admin: "Support Admin",
  support_engineer: "Support Engineer",
  business_admin: "Business Admin",
  client_admin: "Client Admin",
  client_user: "Client User",
  business_user: "Business User",
  approver: "Approver",
  viewer: "Viewer",
};

export const PRIORITIES = ["P1", "P2", "P3", "P4", "P5"];
export const PRIORITY_LABEL = {
  P1: "P1 — Immediate",
  P2: "P2 — High",
  P3: "P3 — Normal",
  P4: "P4 — Low",
  P5: "P5 — Planned",
};

export const SEVERITIES = ["critical", "major", "moderate", "minor"];

export const TICKET_TYPES = [
  "incident",
  "bug",
  "service_request",
  "change_request",
  "enhancement",
  "access_request",
  "data_request",
  "training_request",
  "infrastructure_issue",
  "security_issue",
];

export const STATUS_GROUPS = {
  intake: [
    "draft",
    "submitted",
  ],
  assigned: [
    "assigned",
    "accepted",
  ],
  working: [
    "in_progress",
    "escalated",
  ],
  waiting: [
    "waiting_for_client",
    "waiting_for_access",
    "waiting_for_vendor",
    "on_hold",
  ],
  resolution: [
    "resolution_submitted",
  ],
  terminal: ["closed", "rejected", "cancelled", "reopened"],
};

export const ALL_STATUSES = Object.values(STATUS_GROUPS).flat();

export const STATUS_LABEL = ALL_STATUSES.reduce((acc, s) => {
  acc[s] = s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return acc;
}, {});

// Historical workflow statuses are retained only for rendering
// older tickets. They are not part of ALL_STATUSES and therefore
// cannot be selected from normal ticket filters.
Object.assign(STATUS_LABEL, {
  returned_to_engineer: "Returned To Engineer",
  reference_pending: "Reference pending",
  resolution_approved: "Resolution Approved",
  resolution_rejected: "Rejected",
  under_investigation: "In Progress",
  waiting_for_client: "Client confirmation pending",
  waiting_for_access: "Reference pending",
  waiting_for_vendor: "Vendor confirmation pending",
  pending_business_approval: "Legacy: Pending Business Approval",
  approved_for_support: "Legacy: Approved for Support",
  support_review: "Legacy: Support Review",
  waiting_for_approval: "Legacy: Waiting for Approval",
  pending_internal_approval: "Legacy: Pending Internal Approval",
  pending_business_validation: "Legacy: Pending Business Validation",
  pending_closure_approval: "Legacy: Pending Closure Approval",
});

// Chart rows grouped by display label: legacy and current workflow
// statuses can share a label (e.g. waiting_for_access / reference_pending),
// so they are shown as one bar whose `status` lists every underlying value.
export function groupStatusCounts(rows = []) {
  const bars = {};
  rows.forEach((row) => {
    const name =
      STATUS_LABEL[row._id] ||
      (row._id || "Unknown").replace(/_/g, " ");
    const bar = bars[name] || (bars[name] = { name, value: 0, statuses: [] });
    bar.value += row.count || 0;
    if (row._id) bar.statuses.push(row._id);
  });
  return Object.values(bars)
    .sort((a, b) => b.value - a.value)
    .map((bar) => ({ ...bar, status: [...bar.statuses].sort().join(",") }));
}

// Status filter choices: one per displayed name. Legacy and current statuses
// that share a name (e.g. "Reference pending", "Rejected") are filtered
// together. Values are sorted so they match the chart bars' links.
export const STATUS_FILTER_OPTIONS = Object.values(
  [
    ...ALL_STATUSES,
    "reference_pending",
    "under_investigation",
    "resolution_approved",
    "resolution_rejected",
  ].reduce((options, status) => {
    const label = STATUS_LABEL[status];
    (options[label] ||= { label, statuses: [] }).statuses.push(status);
    return options;
  }, {})
).map((option) => ({
  label: option.label,
  value: [...option.statuses].sort().join(","),
}));

export const STATUS_TONE = {
  draft: "slate",
  submitted: "blue",
  pending_business_approval: "amber",
  returned_for_information: "amber",
  approved_for_support: "gold",
  support_review: "gold",
  assigned: "blue",
  accepted: "blue",
  assignment_rejected: "red",
  in_progress: "blue",
  under_investigation: "blue",
  development_required: "blue",
  testing_in_progress: "blue",
  scheduled_for_release: "blue",
  waiting_for_client: "amber",
  waiting_for_access: "amber",
  waiting_for_vendor: "amber",
  waiting_for_approval: "amber",
  on_hold: "slate",
  escalated: "red",
  resolution_submitted: "gold",
  pending_internal_approval: "gold",
  returned_to_engineer: "amber",
  pending_business_validation: "gold",
  resolution_rejected: "red",
  resolution_approved: "green",
  pending_closure_approval: "gold",
  closed: "green",
  rejected: "red",
  duplicate: "slate",
  cancelled: "slate",
  reopened: "red",
};

export const PRIORITY_TONE = {
  P1: "red",
  P2: "orange",
  P3: "gold",
  P4: "blue",
  P5: "slate",
};

export const LOGO_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_3f4e74cb-e55c-426c-bea9-447f28885dc0/artifacts/fmya3g89_logo.png";
